import { createPendingViewSnapshot } from "lib/actions/views/createPendingViewSnapshot";
import { createContext, useCallback, use, useMemo, useState } from "react";

type SnapshotUploadStatus = "pending" | "succeeded" | "failed";
type SnapshotItem = {
  projectId: number;
  image: Blob;
  uploadStatus: SnapshotUploadStatus;
};

type ContextType = {
  getSnapshotUploadLocal(snapshotId: string): SnapshotItem | null;
  getAnnotatedSnapshotUploadLocal(
    snapshotId: string,
    annotatedSnapshotId: string,
  ): SnapshotItem | null;
  addSnapshot(
    projectId: number,
    snapshotId: string,
    image: Blob,
  ): Promise<void>;
  addAnnotatedSnapshot(
    projectId: number,
    snapshotId: string,
    annotatedSnapshotId: string,
    image: Blob,
  ): Promise<void>;
};

export const ViewsSnapshotUploadContext = createContext<ContextType>(null);

export const useViewsSnapshotUploads = () => use(ViewsSnapshotUploadContext);

export const ViewsSnapshotUploadProvider = ({
  children,
}: React.PropsWithChildren) => {
  const value = useProvider();

  return (
    <ViewsSnapshotUploadContext value={value}>
      {children}
    </ViewsSnapshotUploadContext>
  );
};

const useProvider = (): ContextType => {
  const [snapshotUploads, setSnapshotUploads] = useState<
    Map<string, SnapshotItem>
  >(new Map());
  const [annotatedSnapshotUploads, setAnnotatedSnapshotUploads] = useState<
    Map<string, SnapshotItem>
  >(new Map());

  const addSnapshot = useCallback(
    async (projectId: number, snapshotId: string, image: Blob) => {
      const update = (uploadStatus: SnapshotUploadStatus) =>
        setSnapshotUploads((prev) =>
          new Map(prev).set(snapshotId, {
            projectId,
            image,
            uploadStatus,
          }),
        );
      update("pending");
      try {
        await createPendingViewSnapshot(projectId, snapshotId, image);
        update("succeeded");
      } catch {
        update("failed");
      }
    },
    [],
  );

  const getSnapshotUploadLocal = useCallback(
    (snapshotId: string): SnapshotItem | null =>
      snapshotUploads.get(snapshotId) ?? null,
    [snapshotUploads],
  );

  const getAnnotatedSnapshotUploadLocal = useCallback(
    (snapshotId: string, annotatedSnapshotId: string): SnapshotItem | null =>
      annotatedSnapshotUploads.get(
        `${snapshotId}-annotated-${annotatedSnapshotId}`,
      ) ?? null,
    [annotatedSnapshotUploads],
  );

  const addAnnotatedSnapshot = useCallback(
    async (
      projectId: number,
      snapshotId: string,
      annotatedSnapshotId: string,
      image: Blob,
    ) => {
      const key = `${snapshotId}-annotated-${annotatedSnapshotId}`;
      const update = (uploadStatus: SnapshotUploadStatus) =>
        setAnnotatedSnapshotUploads((prev) =>
          new Map(prev).set(key, {
            projectId,
            image,
            uploadStatus,
          }),
        );
      update("pending");
      try {
        await createPendingViewSnapshot(
          projectId,
          snapshotId,
          image,
          annotatedSnapshotId,
        );
        update("succeeded");
      } catch {
        update("failed");
      }
    },
    [],
  );

  return useMemo(
    () => ({
      getSnapshotUploadLocal,
      getAnnotatedSnapshotUploadLocal,
      addSnapshot,
      addAnnotatedSnapshot,
    }),
    [
      getSnapshotUploadLocal,
      getAnnotatedSnapshotUploadLocal,
      addSnapshot,
      addAnnotatedSnapshot,
    ],
  );
};
